import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowBack, MilitaryTech, Star, Visibility } from "@mui/icons-material";
import { getFantasySimpleTeams, getSeasons } from "../../services/FantasyService";
import { FantasyTeam } from "../../models/FantasyTeam";
import Loading from "../../components/Loading";
import PlayerAvatar from "../../components/PlayerAvatar";
import { useBadges } from "../../services/BadgeService";
import { getSimpleDivisionLabel } from "../../utils/divisionUtils";
import styles from "./Fantasy2.module.css";

// Division pill colors, per the redesign
const divPillStyle = (div: string): React.CSSProperties => {
    const map: Record<string, { color: string; bg: string; border: string }> = {
        'ligaen': { color: '#f1c40f', bg: 'rgba(241,196,15,0.12)', border: 'rgba(241,196,15,0.35)' },
        '1div': { color: '#4ecbe0', bg: 'rgba(23,162,184,0.14)', border: 'rgba(23,162,184,0.35)' },
        '2div': { color: '#4ade80', bg: 'rgba(39,174,96,0.12)', border: 'rgba(39,174,96,0.3)' },
        '3div': { color: '#ff9933', bg: 'rgba(255,153,51,0.12)', border: 'rgba(255,153,51,0.3)' },
    };
    const s = map[div] || { color: 'rgba(255,255,255,0.7)', bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.15)' };
    return { color: s.color, background: s.bg, border: `1px solid ${s.border}` };
};

// The "all fantasy teams for one owner" page at /fantasy/view/:steamID/:season.
const ViewAllTeamsPage2 = () => {
    const { steamID, season } = useParams();
    const { t } = useTranslation(['pages', 'common']);
    const navigate = useNavigate();
    const badges = useBadges();
    const [teams, setTeams] = useState<FantasyTeam[]>([]);
    const [seasons, setSeasons] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);

    const seasonNum = Number(season);

    useEffect(() => {
        getSeasons().then((list) => setSeasons(list.sort((a, b) => b - a))).catch(() => { });
    }, []);

    useEffect(() => {
        if (!steamID || !season) return;
        setLoading(true);
        getFantasySimpleTeams(steamID, seasonNum)
            .then((data) => setTeams(data.filter((t) => t.season === seasonNum).sort((a, b) => b.points - a.points)))
            .catch(() => setTeams([]))
            .finally(() => setLoading(false));
    }, [steamID, season, seasonNum]);

    const bestPoints = useMemo(() => teams.length ? Math.max(...teams.map((t) => t.points)) : 0, [teams]);
    const bestTeam = useMemo(() => teams.find((t) => t.points === bestPoints), [teams, bestPoints]);
    const totalPoints = useMemo(() => teams.reduce((acc, t) => acc + t.points, 0), [teams]);
    const ownerName = teams[0]?.account?.playerName ?? '';


    if (!steamID || !season) {
        return <div className={`${styles.page} ${styles.pageNarrow}`}><p className={styles.emptyState}>{t('pages:fantasy.loadError')}</p></div>;
    }

    return (
        <div className={`${styles.page} ${styles.pageNarrow}`}>
            <Link to="/fantasy" className={styles.backLink}>
                <ArrowBack sx={{ fontSize: 18 }} />
                {t('pages:fantasy.backToLeaderboard')}
            </Link>

            <div className={styles.pageHeader}>
                <div className={styles.headerTop}>
                    <div className={styles.ownerHeading}>
                        {steamID && (
                            <PlayerAvatar steamId={steamID} name={ownerName} size={46} badges={badges} />
                        )}
                        <div>
                            <h1 className={styles.title}>{t('pages:fantasy.teamsTitle', { name: ownerName || '…' })}</h1>
                            <p className={styles.subtitle}>
                                {t('pages:fantasy.teamsSubtitle')} · <span style={{ color: '#f15c25', fontWeight: 700 }}>{t('pages:fantasy.teamsCount', { count: teams.length })}</span>
                            </p>
                        </div>
                    </div>
                    <div>
                        <label className={styles.selectLabel}>{t('common:selectSeason')}</label>
                        <select
                            className={styles.select}
                            value={seasonNum}
                            onChange={(e) => navigate(`/fantasy/view/${steamID}/${e.target.value}`)}
                        >
                            {(seasons.length ? seasons : [seasonNum]).map((s) => (
                                <option key={s} value={s}>{t('common:seasonNum', { number: s })}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center" style={{ marginTop: 30 }}><Loading /></div>
            ) : teams.length === 0 ? (
                <p className={styles.emptyState}>{t('pages:fantasy.teamsNone')}</p>
            ) : (
                <>
                    <div className={styles.tileRow}>
                        <div className={styles.tileFlex}>
                            <div className={styles.tileLabel}>{t('pages:fantasy.totalTeams')}</div>
                            <div className={styles.tileValue} style={{ marginTop: 6, fontSize: '1.7rem' }}>{teams.length}</div>
                        </div>
                        <div className={styles.tileFlex}>
                            <div className={styles.tileLabel}>{t('pages:fantasy.totalPoints')}</div>
                            <div className={`${styles.tileValue} ${styles.tileValueAccent}`} style={{ marginTop: 6, fontSize: '1.7rem' }}>
                                {totalPoints.toLocaleString('da-DK')}
                            </div>
                        </div>
                        <div className={styles.tileFlex}>
                            <div className={styles.tileLabel}>{t('pages:fantasy.bestTeam')}</div>
                            <div className={styles.tileValueRow} style={{ marginTop: 6 }}>
                                <span className={styles.tileValueName} style={{ fontSize: '1.2rem' }}>{bestTeam ? getSimpleDivisionLabel(bestTeam.division) : '–'}</span>
                                <span className={styles.tileValueSmall}>{bestPoints.toLocaleString('da-DK')}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.teamGrid}>
                        {teams.map((team) => (
                            <div className={styles.teamCard} key={team.id}>
                                <div className={styles.teamCardTop}>
                                    <span className={styles.divisionPill} style={divPillStyle(team.division)}>
                                        <MilitaryTech sx={{ fontSize: 14 }} />
                                        {getSimpleDivisionLabel(team.division)}
                                    </span>
                                    {team.points === bestPoints && teams.length > 1 && (
                                        <Star className={styles.bestStar} sx={{ fontSize: 20 }} titleAccess={t('pages:fantasy.bestTeam')} />
                                    )}
                                </div>
                                <div className={styles.teamCardName}>
                                    {t('pages:fantasy.teamCardName', { name: team.account?.playerName ?? ownerName, division: getSimpleDivisionLabel(team.division) })}
                                </div>
                                <div className={styles.teamCardPoints}>
                                    <span className={styles.teamCardPointsValue}>{team.points.toLocaleString('da-DK')}</span>
                                    <span className={styles.teamCardPointsLabel}>{t('common:points').toLowerCase()}</span>
                                </div>
                                <Link to={`/fantasy/view/${steamID}/${season}/${team.division}`} className={styles.teamCardButton}>
                                    <Visibility sx={{ fontSize: 18 }} />
                                    {t('pages:fantasy.seeTeam')}
                                </Link>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default ViewAllTeamsPage2;
